import React from 'react';
import { useTranslation } from 'react-i18next';
import { Filter } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

interface TicketFiltersProps {
  showFilters: boolean;
  onToggleFilters: () => void;
  filters: {
    status: string[];
    priority: string[];
    trade: string[];
    planLevel: string[];
    assigned: string[];
  };
  onFilterChange: (field: string, value: string, checked: boolean) => void;
  onResetFilters: () => void;
  gewerke: any[];
  plans: any[];
  teamMembers: any[];
  statusData?: any[];
  priorityData?: any[];
  hideButton?: boolean;
  embedded?: boolean;
}

export const TicketFilters: React.FC<TicketFiltersProps> = ({
  showFilters,
  onToggleFilters,
  filters,
  onFilterChange,
  onResetFilters,
  gewerke,
  plans,
  teamMembers,
  statusData = [],
  priorityData = [],
  hideButton = false,
  embedded = false
}) => {
  const { t } = useTranslation(['pages.projectDetails']);
  const hasActiveFilters = Boolean(
    filters.status.length > 0 ||
    filters.priority.length > 0 ||
    filters.trade.length > 0 ||
    filters.planLevel.length > 0 ||
    filters.assigned.length > 0
  );

  return (
    <>
      {/* Filter Button */}
      {!hideButton && (
        <button
          onClick={onToggleFilters}
          className={`button button--icon ${showFilters ? 'button--primary' : 'button--outline'}`}
          title={t('filters.tickets')}
        >
          <Filter className="h-4 w-4" />
        </button>
      )}

      {/* Filter Section */}
      {showFilters && (
        <div className={embedded ? 'h-full bg-[#E8E8E8] flex flex-col' : 'mb-6 overflow-hidden rounded-2xl border-0 bg-[#E8E8E8] shadow-md'}>
          <div className="flex items-center justify-between border-b border-gray-300 bg-[#E8E8E8] px-4 py-3">
            <h3 className="text-sm font-semibold text-black">{t('filters.tickets')}</h3>
            {hasActiveFilters && (
              <button
                onClick={onResetFilters}
                className="text-xs text-blue-600 hover:text-blue-700"
              >
                {t('filters.reset')}
              </button>
            )}
          </div>

          <div className={embedded ? 'flex-1 overflow-y-auto space-y-4 p-4' : 'grid grid-cols-1 gap-4 p-4 md:grid-cols-2 lg:grid-cols-3'}>
            {/* Status Filter */}
            <fieldset className="rounded-lg border border-gray-300 bg-white p-4">
              <legend className="px-2 text-sm font-medium text-black">{t('filters.status')}</legend>
              <div className="mt-2 space-y-2">
                {statusData && statusData.length > 0 ? (
                  statusData.map((status: any) => (
                    <div key={status.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`tickets-status-${status.id}`}
                        checked={filters.status.includes(status.id)}
                        onCheckedChange={(checked) => onFilterChange('status', status.id, Boolean(checked))}
                      />
                      <label
                        htmlFor={`tickets-status-${status.id}`}
                        className="text-sm text-slate-700 cursor-pointer flex items-center gap-2"
                      >
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: status.color || '#6b7280' }} />
                        {status.displayName ?? status.name}
                      </label>
                    </div>
                  ))
                ) : (
                  <div className="text-xs text-slate-500">{t('filters.all')}</div>
                )}
              </div>
            </fieldset>

            {/* Priority Filter */}
            <fieldset className="rounded-lg border border-gray-300 bg-white p-4">
              <legend className="px-2 text-sm font-medium text-black">{t('filters.priority')}</legend>
              <div className="mt-2 space-y-2">
                {priorityData && priorityData.length > 0 ? (
                  priorityData.map((priority: any) => (
                    <div key={priority.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`tickets-priority-${priority.id}`}
                        checked={filters.priority.includes(priority.id)}
                        onCheckedChange={(checked) =>
                          onFilterChange('priority', priority.id, Boolean(checked))
                        }
                      />
                      <label
                        htmlFor={`tickets-priority-${priority.id}`}
                        className="text-sm text-slate-700 cursor-pointer flex items-center gap-2"
                      >
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: priority.color || '#6b7280' }} />
                        {priority.displayName || priority.name}
                      </label>
                    </div>
                  ))
                ) : (
                  <div className="text-xs text-slate-500">{t('filters.all')}</div>
                )}
              </div>
            </fieldset>

            {/* Trade Filter */}
            <fieldset className="rounded-lg border border-gray-300 bg-white p-4">
              <legend className="px-2 text-sm font-medium text-black">{t('filters.trade')}</legend>
              <div className="mt-2 space-y-2">
                {gewerke && gewerke.map((gewerk: any) => (
                  <div key={gewerk.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`tickets-trade-${gewerk.id}`}
                      checked={filters.trade.includes(gewerk.id)}
                      onCheckedChange={(checked) => onFilterChange('trade', gewerk.id, Boolean(checked))}
                    />
                    <label htmlFor={`tickets-trade-${gewerk.id}`} className="text-sm text-slate-700 cursor-pointer">
                      {gewerk.displayName || gewerk.name}
                    </label>
                  </div>
                ))}
              </div>
            </fieldset>

            {/* Plan Level Filter */}
            <fieldset className="rounded-lg border border-gray-300 bg-white p-4">
              <legend className="px-2 text-sm font-medium text-black">{t('filters.planLevel')}</legend>
              <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                {plans && plans.map((plan: any) => (
                  <div key={plan.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`tickets-plan-${plan.id}`}
                      checked={filters.planLevel.includes(plan.id)}
                      onCheckedChange={(checked) => onFilterChange('planLevel', plan.id, Boolean(checked))}
                    />
                    <label htmlFor={`tickets-plan-${plan.id}`} className="text-sm text-slate-700 cursor-pointer">
                      {plan.fileName || plan.name}
                    </label>
                  </div>
                ))}
              </div>
            </fieldset>

            {/* Assigned Filter */}
            <fieldset className="rounded-lg border border-gray-300 bg-white p-4">
              <legend className="px-2 text-sm font-medium text-black">{t('filters.assigned')}</legend>
              <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                {teamMembers && teamMembers.map((member: any) => {
                  // Use id (ticket_assignees.id) to match ticket.assignedContractorId
                  const memberId = member.id || member.user_id;
                  return (
                    <div key={memberId} className="flex items-center space-x-2">
                      <Checkbox
                        id={`tickets-assigned-${memberId}`}
                        checked={filters.assigned.includes(memberId)}
                        onCheckedChange={(checked) =>
                          onFilterChange('assigned', memberId, Boolean(checked))
                        }
                      />
                      <label
                        htmlFor={`tickets-assigned-${memberId}`}
                        className="text-sm text-slate-700 cursor-pointer"
                      >
                        {member.display_name || member.name || member.email || '-'}
                      </label>
                    </div>
                  );
                })}
              </div>
            </fieldset>
          </div>
        </div>
      )}
    </>
  );
};
